import { create } from 'zustand';
import { fetchGetChannels } from '@/service/api/application';

interface ChannelState {
  /** 可选择的发行渠道列表 */
  channels: Api.Application.ChannelResponse[];
  /** 是否正在加载中 */
  loading: boolean;
  /** 错误信息 */
  error: string | null;

  /** 获取发行渠道列表 (支持 force 强制刷新) */
  fetchChannels: (force?: boolean) => Promise<Api.Application.ChannelResponse[]>;
  /** 根据 id 或 channel_code 获取渠道详情对象 */
  getChannelById: (id?: string) => Api.Application.ChannelResponse | undefined;
  /** 根据 id 或 channel_code 获取渠道名称 */
  getChannelNameById: (id?: string, fallback?: string) => string;
  /** 清除错误信息 */
  clearError: () => void;
}

let fetchChannelsPromise: Promise<Api.Application.ChannelResponse[]> | null = null;

export const useChannelStore = create<ChannelState>((set, get) => ({
  channels: [],
  loading: false,
  error: null,

  fetchChannels: async (force = false) => {
    const { channels } = get();
    if (!force && channels.length > 0) {
      return channels;
    }
    if (fetchChannelsPromise) {
      return fetchChannelsPromise;
    }

    set({ loading: true, error: null });
    fetchChannelsPromise = fetchGetChannels()
      .then((res) => {
        const list = Array.isArray(res) ? res : [];
        set({ channels: list, loading: false, error: null });
        return list;
      })
      .catch((err) => {
        const errorMessage = err instanceof Error ? err.message : '获取发行渠道列表失败';
        set({ loading: false, error: errorMessage });
        throw err;
      })
      .finally(() => {
        fetchChannelsPromise = null;
      });

    return fetchChannelsPromise;
  },

  getChannelById: (id) => {
    if (!id) return undefined;
    const { channels } = get();
    return channels.find((item) => item.id === id || item.channel_code === id);
  },

  getChannelNameById: (id, fallback = '') => {
    if (!id) return fallback;
    const { channels } = get();
    const target = channels.find((item) => item.id === id || item.channel_code === id);
    return target?.channel_name || fallback || id;
  },

  clearError: () => set({ error: null }),
}));
